// ─────────────────────────────────────────────────────────────────────────
// Plan comparison matrix for the LIVE pricing page (the flag-on
// PricingTiersSection — Free / Pro / Grow / Scale).
//
// WHY THIS FILE EXISTS
// The live page renders per-plan feature LISTS that are cumulative in prose
// ("Everything in Pro", "Everything in Grow"). That reads fine on a wide screen
// where four cards sit side by side, but on a phone the cards stack and the same
// feature never lines up across plans — the mobile-comparison ticket. A table
// needs the same facts expressed as ROWS, which is what this is.
//
// EVERY VALUE HERE IS DERIVED, NOT AUTHORED.
// The rows restate what the live cards already claim (PRICING_TIERS.features in
// ./credit-tiers) and what the backend actually enforces (PLANS in @/lib/plans,
// read by lib/plans/entitlements.ts). No row makes a claim the cards don't
// already make. The one thing a table adds is the explicit "✗" — a card that
// simply omits a feature implies its absence, and a comparison row has to state
// it. That is the point of the ticket, not new marketing.
//
// DRIFT IS A TEST FAILURE, NOT A REVIEW ITEM.
// tests/unit/lib/plan-comparison.test.ts pins the entitlement rows against PLANS
// and the credit rows against PLAN_BASE_CREDITS. If someone changes a plan's
// limits and forgets this file, that test fails. The previous pricing surface
// drifted exactly this way (its table said "5 / 10 / 50 projects" while the plan
// data said 1000), so the guard is the point.
// ─────────────────────────────────────────────────────────────────────────

import type { PricingTierId } from "./credit-tiers";

/** Column order for every `values` tuple below. */
export const COMPARISON_TIER_ORDER: readonly PricingTierId[] = [
  "free",
  "pro",
  "grow",
  "scale",
];

/**
 * A cell is either a yes/no mark or a short string. Strings are used wherever
 * "yes" would lose information the visitor is actually comparing on (how many
 * credits, how many projects, which template library).
 */
export type ComparisonValue = boolean | string;

export interface PlanComparisonRow {
  /** Kept short on purpose — the label column is ~120px at a 360px viewport. */
  label: string;
  /** [free, pro, grow, scale] — see COMPARISON_TIER_ORDER. */
  values: [ComparisonValue, ComparisonValue, ComparisonValue, ComparisonValue];
  /** Longer wording shown as a tooltip/aria description where the short label
   *  drops a qualifier that matters (e.g. the Free tier's demo database). */
  note?: string;
}

export interface PlanComparisonSection {
  heading: string;
  rows: PlanComparisonRow[];
}

export const PLAN_COMPARISON: PlanComparisonSection[] = [
  {
    heading: "Build",
    rows: [
      {
        // Free's 300 is a one-time starter grant capped at FREE_DAILY_CAP/day
        // for FREE_WINDOW_DAYS (lib/plans.ts) — NOT a monthly allowance, so it
        // must not be written as "300/mo" beside the paid tiers.
        label: "Credits included",
        values: ["300 to start (50/day)", "300/mo", "1,000/mo", "3,000/mo"],
        note: "Paid plans include this many credits each month; you can pick a higher amount on any paid plan. Free is a one-time 300-credit grant, capped at 50 credits a day.",
      },
      {
        label: "Projects",
        values: ["3", "Unlimited", "Unlimited", "Unlimited"],
      },
      {
        label: "Templates",
        values: ["Business-ready", "Premium", "Premium", "Premium"],
      },
      { label: "Cloud hosting", values: [true, true, true, true] },
      { label: "Panda subdomain", values: [true, true, true, true] },
      {
        // PLANS.persistentData + PLANS.customerAccounts are both false on Free:
        // appGetsProductionDatabase never provisions a real DB for a free app,
        // so sign-in and saved data are a labeled demo that resets.
        label: "Database & customer logins",
        values: ["Demo only", true, true, true],
        note: "On Free, customer login and saved data are a demo and reset. Paid plans get a real database.",
      },
      { label: "Custom domain", values: [false, true, true, true] },
      { label: "Accept payments", values: [false, true, true, true] },
      { label: "Remove Panda branding", values: [false, true, true, true] },
    ],
  },
  {
    heading: "Grow",
    rows: [
      { label: "AI social posts", values: [false, false, true, true] },
      {
        label: "WhatsApp & email messages",
        values: [false, false, true, true],
      },
      {
        label: "Meta ads: copy, briefs & creatives",
        values: [false, false, true, true],
        note: "Ad copy, creative briefs, and static + video ad creatives for Meta.",
      },
      {
        label: "Customer targeting & next steps",
        values: [false, false, true, true],
      },
    ],
  },
  {
    heading: "Teams & support",
    rows: [
      {
        label: "Team workspaces & roles",
        values: [false, false, false, true],
        note: "Team workspaces with roles and permissions.",
      },
      {
        label: "Internal publishing",
        values: [false, false, false, true],
        note: "Internal publishing plus personal projects.",
      },
      {
        label: "Volume credit discounts",
        values: [false, false, false, true],
      },
      { label: "SSO, SCIM & audit logs", values: [false, false, false, true] },
      {
        // PLANS.prioritySupport is true on scale only.
        label: "Dedicated support",
        values: [false, false, false, true],
        note: "Dedicated support and onboarding.",
      },
    ],
  },
];

/** Flat row list — used by tests and by the mobile table's row rendering. */
export const PLAN_COMPARISON_ROWS: PlanComparisonRow[] =
  PLAN_COMPARISON.flatMap((s) => s.rows);

/** Column index for a tier id, for reading a row's `values`. */
export function comparisonColumnIndex(tier: PricingTierId): number {
  const i = COMPARISON_TIER_ORDER.indexOf(tier);
  if (i < 0) throw new Error(`Unknown pricing tier: ${tier}`);
  return i;
}
